import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { MDBTooltip } from "mdb-react-ui-kit";
import { ChevronDown } from "lucide-react";
import { DataTable } from "../ui";
import PriceDisplay from "../PriceDisplay";
import {
  fetchTrackHubCarriers,
  patchTrackHubCarrier,
} from "../../services/trackHubApi";
import ConfirmModal from "../Modals/ConfirmModal";
import "./trackhub-carriers.css";

function CarrierStatusSelect({
  active,
  disabled,
  title,
  onChange,
  activeLabel,
  inactiveLabel,
  activateDisabled = false,
  deactivateDisabled = false,
}) {
  const select = (
    <div
      className={[
        "trackhub-carrier-status",
        active ? "is-active" : "is-inactive",
        disabled ? "is-disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <select
        value={active ? "active" : "inactive"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "active")}
        aria-label={active ? activeLabel : inactiveLabel}
      >
        <option value="active" disabled={activateDisabled}>
          {activeLabel}
        </option>
        <option value="inactive" disabled={deactivateDisabled}>
          {inactiveLabel}
        </option>
      </select>
      <span className="trackhub-carrier-status__chevron" aria-hidden="true">
        <ChevronDown size={14} strokeWidth={2.25} />
      </span>
    </div>
  );

  if (!title) return select;

  return (
    <MDBTooltip
      tag="span"
      title={title}
      wrapperProps={{ className: "trackhub-carrier-status-tooltip" }}
    >
      {select}
    </MDBTooltip>
  );
}

function displayOrDash(value) {
  if (value == null) return "—";
  const text = String(value).trim();
  return text || "—";
}

function TrackHubCarriersTab({ readOnly = false }) {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar" || i18n.dir() === "rtl";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);

  const loadCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchTrackHubCarriers();
      setRows(result.data || []);
    } catch (error) {
      console.error("Failed to load TrackHub carriers", error);
      toast.error(t("trackHubCarriersPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  const blockedTooltip = useCallback(
    (row) => {
      // Merchant cannot set Inactive when blocked — Admin only.
      if (
        row.deactivateBlockedReason === "LAST_ACTIVE"
        || (row.isActive && row.canDeactivate === false)
      ) {
        return t("trackHubCarriersPage.tooltipInactiveAdminOnly");
      }
      if (row.trackHubAvailable === false) {
        return t("trackHubCarriersPage.tooltipInactiveOnTrackHub");
      }
      return "";
    },
    [t]
  );

  const handleToggle = useCallback(
    async (row, nextActive) => {
      if (Boolean(row.isActive) === Boolean(nextActive)) return;
      if (row.isActive && !nextActive && row.canDeactivate === false) {
        toast.warning(t("trackHubCarriersPage.tooltipInactiveAdminOnly"));
        return;
      }
      if (nextActive && row.trackHubAvailable === false) {
        toast.warning(t("trackHubCarriersPage.inactiveOnTrackHub"));
        return;
      }
      setSavingId(row.carrierId);
      try {
        const result = await patchTrackHubCarrier(row.carrierId, {
          isActive: nextActive,
          ...(nextActive === false && row.cancelPendingPayments
            ? { cancelPendingPayments: true }
            : {}),
        });
        await loadCarriers();
        const actualActive = result?.data?.isActive === true;
        toast.success(
          actualActive
            ? t("trackHubCarriersPage.activated")
            : t("trackHubCarriersPage.deactivated")
        );
      } catch (error) {
        const code = error?.response?.data?.code;
        if (code === "PENDING_PAYMENT_SHIPMENTS" && nextActive === false) {
          setPendingDeactivate({
            row,
            count: error?.response?.data?.pendingCount,
          });
          return;
        }
        const apiData = error?.response?.data?.data;
        if (apiData) {
          setRows((prev) =>
            prev.map((r) =>
              String(r.carrierId) === String(row.carrierId) ? { ...r, ...apiData } : r
            )
          );
        } else {
          await loadCarriers();
        }
        const message =
          error?.response?.data?.message ||
          (code === "LAST_ACTIVE_CARRIER" || code === "CARRIER_ASSIGNED_TO_BRANCHES"
            ? t("trackHubCarriersPage.tooltipInactiveAdminOnly")
            : code === "CARRIER_INACTIVE_ON_TRACKHUB" || code === "CARRIER_INACTIVE"
              ? t("trackHubCarriersPage.inactiveOnTrackHub")
              : t("trackHubCarriersPage.updateError"));
        toast.error(message);
      } finally {
        setSavingId(null);
      }
    },
    [loadCarriers, t]
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: t("trackHubCarriersPage.courierDriver"),
        sortable: true,
        strong: true,
        minWidth: 180,
        render: (row) => row.name || row.carrierId,
      },
      {
        id: "coverage",
        header: t("trackHubCarriersPage.coverage"),
        minWidth: 120,
        render: (row) => displayOrDash(row.coverage),
      },
      {
        id: "pricing",
        header: t("trackHubCarriersPage.priceRange"),
        sortable: true,
        minWidth: 140,
        render: (row) => {
          const amount = row.startingPrice?.amount;
          if (amount == null || !Number.isFinite(Number(amount))) {
            return t("trackHubCarriersPage.pricingNa");
          }
          return (
            <PriceDisplay
              value={Number(amount)}
              iconSize={12}
              color="#030f1b"
              className="trackhub-carrier-price"
            />
          );
        },
      },
      {
        id: "avgTime",
        header: t("trackHubCarriersPage.avgTime"),
        minWidth: 120,
        render: (row) => displayOrDash(row.avgTimeDescription),
      },
      {
        id: "contact",
        header: t("trackHubCarriersPage.contact"),
        minWidth: 160,
        render: (row) => displayOrDash(row.contact),
      },
      {
        id: "active",
        header: t("trackHubCarriersPage.active"),
        minWidth: 130,
        render: (row) => {
          const deactivateBlocked = row.isActive && row.canDeactivate === false;
          const trackHubBlocked = !row.isActive && row.trackHubAvailable === false;
          const lastActiveBlocked = row.deactivateBlockedReason === "LAST_ACTIVE";
          // Keep dropdown enabled; block invalid deactivation via toast on change.
          const disabled = readOnly || savingId === row.carrierId;
          return (
            <CarrierStatusSelect
              active={Boolean(row.isActive)}
              disabled={disabled}
              activateDisabled={trackHubBlocked}
              deactivateDisabled={lastActiveBlocked || deactivateBlocked}
              title={
                lastActiveBlocked || deactivateBlocked || trackHubBlocked
                  ? blockedTooltip(row)
                  : undefined
              }
              activeLabel={t("trackHubCarriersPage.statusActive")}
              inactiveLabel={t("trackHubCarriersPage.statusInactive")}
              onChange={(next) => handleToggle(row, next)}
            />
          );
        },
      },
    ],
    [blockedTooltip, handleToggle, readOnly, savingId, t]
  );

  return (
    <div className="trackhub-carriers-tab">
      <DataTable
        className="trackhub-carriers-table"
        tableId="trackhub-carriers-v2"
        title={t("trackHubCarriersPage.tableTitle")}
        showColumnSettings={false}
        maxBodyHeight={648}
        columns={columns}
        data={rows}
        rowKey={(row) => String(row.carrierId)}
        loading={loading}
        isRtl={isArabic}
        emptyState={t("trackHubCarriersPage.empty")}
      />
      <ConfirmModal
        show={Boolean(pendingDeactivate)}
        onHide={() => setPendingDeactivate(null)}
        title={t("trackHubCarriersPage.pendingPaymentTitle")}
        message={t("trackHubCarriersPage.pendingPaymentMessage", {
          count: pendingDeactivate?.count || 0,
        })}
        action={() => {
          const row = pendingDeactivate?.row;
          setPendingDeactivate(null);
          if (row) handleToggle({ ...row, cancelPendingPayments: true }, false);
        }}
      />
    </div>
  );
}

export default TrackHubCarriersTab;
